/**
 * Iter-62.3.2 — canonical U.S. phone formatter + validator shared by:
 *   - Standard `LandingForm.jsx`
 *   - `QuizForm.jsx`
 *   - `FocusedFunnelForm.jsx`
 *
 * DISPLAY CONTRACT (consumer-facing input):
 *   - 10 digits         → `585-545-8888`
 *   - 11 digits (1NPA…) → `1-585-545-8888`
 *   - Fewer digits render a progressive prefix of the same pattern so
 *     backspace / edit / paste feel natural on desktop and mobile.
 *
 * STORAGE / DELIVERY CONTRACT (unchanged):
 *   Backend already strips non-digits and drops the leading `1` via
 *   `util.normalize_phone` / `bot_defense._PHONE_DIGITS_RX`. We do NOT
 *   fabricate digits and we do NOT auto-prepend a country code — the
 *   platform's canonical `phone_digits` normalization stays authoritative.
 *
 * VALIDATION CONTRACT:
 *   `isValidUSPhone` returns true only when the input yields exactly 10
 *   digits (or 11 digits starting with `1`). Formatting characters
 *   `- ( ) . space +` are stripped before length check so they cannot
 *   make a bad number look valid.
 */

const DIGITS_RX = /\D/g;

/**
 * Return the raw digits from a raw phone string with the leading
 * country-code `1` stripped when present. Never fabricates digits.
 * @param {string | null | undefined} raw
 * @returns {string} digits only, length 0-10 for a well-formed input
 */
export function normalizeUSPhoneDigits(raw) {
  const digits = String(raw || "").replace(DIGITS_RX, "");
  if (digits.length === 11 && digits.startsWith("1")) return digits.slice(1);
  return digits;
}

/**
 * Format a raw phone string for display. Idempotent (formatting an
 * already-formatted value returns the same string). Cursor / caret
 * behaviour is preserved by returning progressively-longer prefixes
 * as the user types.
 * @param {string | null | undefined} raw
 * @returns {string}
 */
export function formatUSPhone(raw) {
  let digits = String(raw || "").replace(DIGITS_RX, "");
  if (!digits) return "";
  // 11-digit U.S. shape (1 + NPA + NXX + XXXX). Cap at 11 so extra
  // pasted characters after a full number do not leak into the display.
  if (digits[0] === "1") {
    digits = digits.slice(0, 11);
    const rest = digits.slice(1);
    if (rest.length === 0) return "1";
    if (rest.length <= 3) return `1-${rest}`;
    if (rest.length <= 6) return `1-${rest.slice(0, 3)}-${rest.slice(3)}`;
    return `1-${rest.slice(0, 3)}-${rest.slice(3, 6)}-${rest.slice(6, 10)}`;
  }
  // 10-digit shape.
  digits = digits.slice(0, 10);
  if (digits.length <= 3) return digits;
  if (digits.length <= 6) return `${digits.slice(0, 3)}-${digits.slice(3)}`;
  return `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6, 10)}`;
}

/**
 * Strict U.S. phone validity check. Accepts either 10 digits or
 * 11 digits starting with `1`. Formatting characters are stripped
 * before the length check so `(585) 545-8888` and `585-545-8888`
 * both pass, while `585-545-888` (9 real digits) fails.
 * @param {string | null | undefined} raw
 * @returns {boolean}
 */
export function isValidUSPhone(raw) {
  const digits = String(raw || "").replace(DIGITS_RX, "");
  if (digits.length === 10) return true;
  if (digits.length === 11 && digits.startsWith("1")) return true;
  return false;
}
